import { fromB64, toB64 } from '@circa/shared/crypto';
import type {
  PullResponse,
  PushRequest,
  PushResponse,
  SyncPing,
  WireRecord,
} from '@circa/shared/protocol';
import { useSyncExternalStore } from 'react';
import { api, apiUrl, isOfflineError } from './api';
import { type BodyRow, db, getMeta, type RecordRow, setMeta } from './db';
import { newId } from './ids';
import { keys } from './keys';
import {
  applyRemote,
  flush,
  getBody,
  hasPendingWrite,
  invalidateBody,
  onPersisted,
  setBody,
} from './repo';
import { getToken, hasServer, onLocked, onUnlocked } from './session';
import { store } from './store';
import { type AnyEntity, BODY_KINDS, type Kind } from './types';

// Sync engine. Pushes dirty records, pulls everything after the last server revision.
// Triggers: local edits (debounced), a WebSocket ping from the server, focus/online,
// a 60 s timer while visible, and pull-to-refresh. Works offline: edits wait on disk.

export type SyncStatus = 'off' | 'idle' | 'syncing' | 'offline' | 'error';

interface SyncState {
  status: SyncStatus;
  lastSyncAt: number | null;
  error: string | null;
  /** records waiting to go up */
  pending: number;
}

let state: SyncState = { status: 'off', lastSyncAt: null, error: null, pending: 0 };
const listeners = new Set<() => void>();

function set(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export function useSyncState(): SyncState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}

export const getSyncState = () => state;

const pullListeners = new Set<(kinds: Set<string>) => void>();
/** Called after a pull applied remote changes (e.g. the MCP inbox, re-render hints). */
export function onPulled(fn: (kinds: Set<string>) => void): () => void {
  pullListeners.add(fn);
  return () => pullListeners.delete(fn);
}

let running: Promise<void> | null = null;
let again = false;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let interval: ReturnType<typeof setInterval> | null = null;
let ws: WebSocket | null = null;
let wsRetry = 0;
let started = false;
const cleanups: (() => void)[] = [];

/** Sync now (push then pull). Concurrent calls coalesce into one extra round. */
export function syncNow(): Promise<void> {
  if (!hasServer() || !keys.unlocked) return Promise.resolve();
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    set({ status: 'syncing', error: null });
    try {
      do {
        again = false;
        await flush();
        await pushAll();
        await pullAll();
        await uploadFiles();
      } while (again);
      set({ status: 'idle', lastSyncAt: Date.now(), error: null });
    } catch (err) {
      if (isOfflineError(err)) set({ status: 'offline' });
      else set({ status: 'error', error: (err as Error).message });
    } finally {
      running = null;
      void countPending();
    }
  })();
  return running;
}

export function schedulePush(delay = 1200) {
  if (!hasServer()) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void syncNow();
  }, delay);
}

async function countPending() {
  const n = await db.records.where('dirty').equals(1).count();
  if (n !== state.pending) set({ pending: n });
}

// ------------------------------------------------------------------ push

const PUSH_BATCH = 100;

async function pushAll() {
  // Ids to remove from the server only (chats switched to "this device only").
  const forget = await db.forget.toArray();
  if (forget.length) {
    const records: WireRecord[] = forget.map((f) => ({
      id: f.id,
      kind: f.kind,
      vaultId: null,
      updatedAt: f.at,
      deleted: true,
      data: null,
      body: null,
    }));
    await api<PushResponse>('/api/sync/push', { body: { records } satisfies PushRequest });
    await db.forget.bulkDelete(forget.map((f) => f.id));
  }

  for (;;) {
    const dirty = (
      await db.records
        .where('dirty')
        .equals(1)
        .limit(PUSH_BATCH * 3)
        .toArray()
    ).filter((r) => !r.localOnly);
    if (!dirty.length) break;
    const batch = dirty.slice(0, PUSH_BATCH);
    const bodies = new Map<string, BodyRow>();
    for (const b of await db.bodies.bulkGet(batch.map((r) => r.id))) {
      if (b?.dirty) bodies.set(b.id, b);
    }
    const records: WireRecord[] = batch.map((r) => {
      const b = bodies.get(r.id);
      return {
        id: r.id,
        kind: r.kind,
        vaultId: r.vaultId || null,
        updatedAt: r.updatedAt,
        deleted: !!r.deleted,
        data: toB64(r.data),
        body: r.deleted ? null : b ? toB64(b.data) : undefined,
      };
    });
    const res = await api<PushResponse>('/api/sync/push', {
      body: { records } satisfies PushRequest,
    });
    const accepted = new Set(res.accepted);
    await db.transaction('rw', db.records, db.bodies, async () => {
      for (const r of batch) {
        if (!accepted.has(r.id)) continue;
        // Only clear if nothing changed while we were pushing.
        const cur = await db.records.get(r.id);
        if (cur && cur.updatedAt === r.updatedAt) await db.records.update(r.id, { dirty: 0 });
        const b = bodies.get(r.id);
        if (b) {
          const curB = await db.bodies.get(r.id);
          if (curB && curB.updatedAt === b.updatedAt) await db.bodies.update(r.id, { dirty: 0 });
        }
      }
    });
    for (const c of res.conflicts) await applyIncoming(c, true);
    // Rows that were neither accepted nor in conflict would loop forever; stop instead.
    if (!res.accepted.length && !res.conflicts.length) break;
    if (dirty.length <= PUSH_BATCH) break;
  }
}

// ------------------------------------------------------------------ pull

async function pullAll() {
  let since = (await getMeta<number>('sync.rev')) ?? 0;
  const touched = new Set<string>();
  for (let page = 0; page < 200; page++) {
    const res = await api<PullResponse>(`/api/sync/pull?since=${since}&limit=200`);
    for (const r of res.records) {
      await applyIncoming(r, false);
      touched.add(r.kind);
    }
    since = res.rev;
    await setMeta('sync.rev', since);
    if (!res.more) break;
  }
  if (touched.size) for (const l of pullListeners) l(touched);
}

/** Write a server record locally unless our own unsent edit is newer. */
async function applyIncoming(r: WireRecord, fromConflict: boolean) {
  const local = await db.records.get(r.id);
  if (local?.localOnly) return;
  if (local && (local.dirty || hasPendingWrite(r.id)) && local.updatedAt > r.updatedAt) return;
  if (local && !fromConflict && !local.dirty && local.updatedAt === r.updatedAt) return;

  // Both sides edited a document: keep ours as a copy instead of losing it.
  if (local?.dirty && BODY_KINDS.has(local.kind as Kind) && !r.deleted && keys.unlocked) {
    await keepConflictCopy(local);
  }

  const row: RecordRow = {
    id: r.id,
    kind: r.kind,
    vaultId: r.vaultId ?? '',
    updatedAt: r.updatedAt,
    deleted: r.deleted ? 1 : 0,
    dirty: 0,
    localOnly: 0,
    data: r.data ? fromB64(r.data) : (local?.data ?? new Uint8Array()),
  };
  if (!r.data && !local) return;
  await db.records.put(row);
  if (r.body === null) {
    await db.bodies.delete(r.id);
    invalidateBody(r.id);
  } else if (typeof r.body === 'string') {
    await db.bodies.put({ id: r.id, dirty: 0, updatedAt: r.updatedAt, data: fromB64(r.body) });
    invalidateBody(r.id);
  }
  // A tombstone without data (deleted/forgotten elsewhere) still has to leave memory.
  if (r.data || row.deleted) {
    try {
      await applyRemote(row);
    } catch {
      // Unreadable (wrong key / corrupted); leave it on disk.
    }
  }
}

async function keepConflictCopy(local: RecordRow) {
  try {
    const e = store.get(local.kind as Kind, local.id) as
      | (AnyEntity & { title?: string })
      | undefined;
    if (!e) return;
    const body = await getBody(local.kind as Kind, local.id);
    const { create } = await import('./repo');
    const copy = create(
      local.kind as Kind,
      {
        ...(e as object),
        id: newId(),
        title: `${e.title || 'Untitled'} (conflict copy)`,
      } as never,
    );
    if (body !== undefined) setBody(copy.kind, copy.id, body, { touch: false });
  } catch {}
}

// ------------------------------------------------------------------ files

async function uploadFiles() {
  const rows = await db.files.where('uploaded').equals(0).limit(20).toArray();
  for (const f of rows) {
    if (f.format !== 'file') continue;
    await api(`/api/files/${f.id}`, { method: 'PUT', raw: f.data as Uint8Array<ArrayBuffer> });
    await db.files.update(f.id, { uploaded: 1 });
  }
}

// ------------------------------------------------------------------ realtime + triggers

function connectSocket() {
  const token = getToken();
  if (!token || ws) return;
  const url = apiUrl(`/api/sync/ws?token=${encodeURIComponent(token)}`).replace(/^http/, 'ws');
  const absolute = url.startsWith('ws') ? url : `${location.origin.replace(/^http/, 'ws')}${url}`;
  try {
    ws = new WebSocket(absolute);
  } catch {
    ws = null;
    return;
  }
  ws.onopen = () => {
    wsRetry = 0;
  };
  ws.onmessage = async (ev) => {
    try {
      const msg = JSON.parse(String(ev.data)) as SyncPing;
      const rev = (await getMeta<number>('sync.rev')) ?? 0;
      if (msg.t === 'changed' && msg.rev > rev) void syncNow();
    } catch {}
  };
  ws.onclose = () => {
    ws = null;
    if (!started) return;
    wsRetry = Math.min(wsRetry + 1, 6);
    setTimeout(
      () => {
        if (started && document.visibilityState === 'visible') connectSocket();
      },
      1000 * 2 ** wsRetry,
    );
  };
}

function start() {
  if (started) return;
  started = true;
  if (!hasServer()) {
    set({ status: 'off' });
    return;
  }
  set({ status: 'idle' });
  cleanups.push(onPersisted(() => schedulePush()));
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      void syncNow();
      connectSocket();
    } else {
      void flush();
    }
  };
  const onOnline = () => void syncNow();
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  window.addEventListener('pagehide', () => void flush());
  cleanups.push(() => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
  });
  interval = setInterval(() => {
    if (document.visibilityState === 'visible') void syncNow();
  }, 60_000);
  connectSocket();
  void syncNow();
}

function stop() {
  started = false;
  for (const c of cleanups.splice(0)) c();
  if (interval) clearInterval(interval);
  interval = null;
  ws?.close();
  ws = null;
  set({ status: 'off' });
}

/** Mark a record as "this device only" and remove it from the server. */
export async function forgetOnServer(kind: Kind, id: string) {
  await db.forget.put({ id, kind, at: Date.now() });
  await db.records.update(id, { localOnly: 1, dirty: 0 });
  schedulePush(300);
}

/** Wire the engine to the session lifecycle. Call once at startup. */
export function initSync() {
  onUnlocked(() => {
    stop();
    start();
  });
  onLocked(() => stop());
}
